import { forwardRef } from 'react';
import { css, type Theme } from '@emotion/react';

import { icons } from './icons';

import type { IconName } from './icons';
import type { ForwardRefExoticComponent, RefAttributes } from 'react';
import type { BaseIconProps } from './types';

export type IconSize = 'xs' | 'sm' | 'md' | 'lg' | 'inherit';

export type IconProps = BaseIconProps & {
  /**
   * The fontSize applied to the icon. Defaults to 24px, but can be configure to inherit font
   * size.
   */
  type: IconName;
  /**
   * The fontSize applied to the icon. Defaults to md, but can be configure to inherit font
   * size.
   */
  size?: IconSize;
  /**
   * Applies a color attribute to the SVG element.
   */
  htmlColor?: string;
  fontSize?: string;
};

const iconStyles =
  ({ size }: { size?: IconSize }) =>
  (theme: Theme) =>
    css`
      display: inline-block;
      fill: currentColor;
      vertical-align: middle;
      width: 1em;
      height: 1em;
      font-size: ${!size || size === 'inherit' ? 'inherit' : `${theme.iconSizes?.[size]}px`};
    `;

export const Icon = forwardRef<HTMLElement, IconProps>(function Icon(
  { type, htmlColor, size, ...props },
  ref,
) {
  const IconComponent = icons[type] as ForwardRefExoticComponent<
    BaseIconProps & RefAttributes<HTMLElement>
  >;

  return <IconComponent css={iconStyles({ size }) as any} color={htmlColor} ref={ref} {...props} />;
});

Icon.defaultProps = {
  size: 'md',
};
